/**
 * Featured-blog PEEK carousel — section-specific Swiper init for the featured-blog section.
 *
 * Figma parity (Designs mFgU5IzV2QbEKQPrtJPywq · desktop 1712:78570 / mobile 1712:79573):
 * fixed-width slides (258 mobile / 312 desktop — set in _featured-blog.scss) shown with
 * `slidesPerView: 'auto'` so the NEXT card peeks naturally, a single circular nav arrow, no dots.
 * Mirrors the RESULT of the product-recommendations peek carousel.
 *
 * WHY A SEPARATE MODULE (not a generalisation of popular-products-peek.js) — that module is a
 * RE-POINTER: it waits for the shared carousel init (assets/global.js → `.products-slider`) to create
 * a numeric Swiper, then re-points it to 'auto'. Re-using that shared init for the blog cards is
 * RISKY: it hard-couples to a `.popular-product__wrapper` element and product-specific data-* attrs
 * (`data-per-row` / `data-product-count` / …) and throws if they are absent. So instead of wiring the
 * blog markup into the product-recommendations machinery, we mirror the peek as a small, self-
 * contained init scoped to `.featured-blog__swiper` — no shared-init coupling, the
 * `<product-recommendations>` / `.products-slider` hooks are left completely untouched.
 *
 * Self-initialising side-effect module (matches the src/js/objects/global convention). Idempotent.
 */

const SWIPER_SELECTOR = '.featured-blog__swiper.swiper';

// Mirrors $grid-desktop-min in src/scss/variables/_breakpoints.scss — keep in sync.
const DESKTOP_MIN = 1024;

// px values mirror the rem tokens / section padding in _featured-blog.scss. Motto's root is a fixed
// 10px so rem(N) === N px; keep the two in sync if the tokens move. GAP = inter-slide gap; OFFSET =
// first-slide left inset (matches the header inset so the heading and the first card share an edge).
const GAP = { mobile: 16, desktop: 32 };
const OFFSET = { mobile: 24, desktop: 48 };

function initCarousel(swiperEl) {
  if (!window.Swiper) return false;

  // Idempotent — leave a live instance alone (Swiper's own breakpoints handle viewport changes).
  if (swiperEl.swiper && !swiperEl.swiper.destroyed) return true;

  // Don't bother initialising an empty track (no blog selected / no blocks added yet).
  if (!swiperEl.querySelector('.swiper-slide')) return true;

  const carousel =
    swiperEl.closest('.featured-blog__carousel') || swiperEl.parentElement;
  const nextEl = carousel
    ? carousel.querySelector('.swiper-button-next')
    : null;
  const prevEl = carousel
    ? carousel.querySelector('.swiper-button-prev')
    : null;

  // OVERFLOW GATE — a PEEK carousel whose cards all FIT the container needs no nav and no drag: "the
  // page already shows all blog post cards" → zero arrows, no swiper affordance. Swiper's own
  // `watchOverflow` lock is TOO STRICT here — with `slidesOffsetBefore` set it only locks when the
  // container clears the content PLUS twice the offset, so a track that visually fits (last card flush
  // to the edge) still reads as "overflowing" and keeps the arrows shown. So we measure real overflow
  // ourselves from the SAME geometry Swiper lays out with (fixed card widths + inter-slide gap + leading
  // offset) and gate the nav on it. Runs after init and on every Swiper resize (a viewport / breakpoint
  // change flips fits<->overflows). FITS → hide BOTH arrows outright (not just Swiper's greyed disabled
  // state) and turn dragging off; OVERFLOWS → hand nav visibility back to Swiper's disabled-class logic
  // (+ the SCSS that hides whichever one is disabled) and re-enable dragging.
  function syncOverflow(sw) {
    const slides = swiperEl.querySelectorAll('.featured-blog__slide');
    const desktop = window.innerWidth >= DESKTOP_MIN;
    const gap = desktop ? GAP.desktop : GAP.mobile;
    let content = desktop ? OFFSET.desktop : OFFSET.mobile; // leading inset (Swiper's slidesOffsetBefore)
    slides.forEach((slide, i) => {
      content += slide.getBoundingClientRect().width;
      if (i < slides.length - 1) content += gap;
    });
    // content = right edge of the last card. If it fits inside the visible container, nothing scrolls.
    const overflows = content > swiperEl.clientWidth + 1; // +1px tolerance for sub-pixel rounding

    sw.allowTouchMove = overflows;
    [prevEl, nextEl].forEach((el) => {
      if (!el) return;
      if (overflows) {
        el.style.removeProperty('display'); // back to Swiper's disabled-class + SCSS control
      } else {
        el.style.setProperty('display', 'none'); // fits → no nav affordance at all
      }
    });
    if (!overflows) sw.slideTo(0, 0); // undo any partial scroll left from a previously-wider viewport
  }

  // slidesPerView stays 'auto' at every width; only the gap + first-slide offset change at desktop.
  // Swiper applies the breakpoint override itself on resize, so no manual resize handling is needed.
  const swiper = new window.Swiper(swiperEl, {
    slidesPerView: 'auto',
    spaceBetween: GAP.mobile,
    slidesOffsetBefore: OFFSET.mobile,
    loop: false,
    watchOverflow: true, // belt-and-braces; syncOverflow() is the authoritative nav gate
    a11y: { enabled: true },
    navigation:
      nextEl && prevEl
        ? { nextEl, prevEl, disabledClass: 'swiper-button-disabled' }
        : false,
    breakpoints: {
      [DESKTOP_MIN]: {
        spaceBetween: GAP.desktop,
        slidesOffsetBefore: OFFSET.desktop,
      },
    },
    on: {
      resize: syncOverflow,
    },
  });

  syncOverflow(swiper);

  return true;
}

function scan() {
  let allReady = true;
  document.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
    if (!initCarousel(el)) allReady = false;
  });
  return allReady;
}

let booted = false;

function boot() {
  if (booted) return;
  booted = true;

  // Settle window — the vendored Swiper script may load after this bundle. Poll briefly until it's
  // present and every featured-blog carousel is initialised, then stop. ~6s at 150ms.
  let ticks = 0;
  const settle = window.setInterval(() => {
    const done = scan();
    if (done || ++ticks > 40) window.clearInterval(settle);
  }, 150);

  // Theme-editor re-renders replace the section DOM (new element, no .swiper instance) → re-init.
  document.addEventListener('shopify:section:load', () => {
    window.setTimeout(scan, 50);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export default boot;
